'use client';

/**
 * Card — v4 bento card primitive.
 * Optional `hover` uplift and optional in-view fadeUp animation.
 * Renders an inner glow blob (`.v4-card__glow`) tinted by `glow`.
 */

import { motion } from 'framer-motion';
import type { CSSProperties, ReactNode } from 'react';
import { fadeUp } from '../theme/motion';

export interface CardProps {
  children: ReactNode;
  /** Enable hover uplift + stronger border/glow. Default true. */
  hover?: boolean;
  /** Animate the card in on scroll (fade-up). Default false. */
  animate?: boolean;
  /** Stagger delay when animate is on (seconds). */
  delay?: number;
  /** Inner glow color (any CSS color / token). Omit to use the default. */
  glow?: string;
  /** Padding modifier. */
  pad?: 'sm' | 'md' | 'lg';
  className?: string;
  style?: CSSProperties;
}

const PAD_CLASS = {
  sm: 'v4-card--pad-sm',
  md: '',
  lg: 'v4-card--pad-lg',
} as const;

export function Card({
  children,
  hover = true,
  animate = false,
  delay = 0,
  glow,
  pad = 'md',
  className,
  style,
}: CardProps) {
  const classes = [
    'v4-card',
    hover ? 'v4-card--hover' : '',
    PAD_CLASS[pad],
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const inner = (
    <>
      <span
        className="v4-card__glow"
        aria-hidden
        style={glow ? { background: glow } : undefined}
      />
      <div style={{ position: 'relative', zIndex: 1, height: '100%' }}>
        {children}
      </div>
    </>
  );

  if (animate) {
    return (
      <motion.div {...fadeUp(delay)} className={classes} style={style}>
        {inner}
      </motion.div>
    );
  }

  return (
    <div className={classes} style={style}>
      {inner}
    </div>
  );
}

export default Card;
